import { useTranslation } from 'react-i18next';

import { collectXValues, formatDateAxisValue, seriesDisplayLabel } from './dataviz-utils';

import type { ResolvedDataVizData } from '@agavi/blocks';

/** Pure, mode-agnostic rendering of the accessible data table — how it's revealed (always visible,
 * behind a toggle, or a slide-out) is decided by DataVizDisplay, not here. */
export function DataVizTableContent({ data }: { data: ResolvedDataVizData }) {
  const { t } = useTranslation();
  const xValues = collectXValues(data.series);
  const pointMaps = data.series.map((s) => new Map(s.points.map((p) => [p.x, p.y])));

  return (
    <table className='w-full border-collapse text-sm'>
      {/* No visible title on the table (the chart above already shows it) — kept sr-only so the
          table still announces a name to screen readers navigating table-by-table. */}
      <caption className='sr-only'>{t('blocks.dataviz.table.caption')}</caption>
      <thead>
        <tr>
          <th scope='col' className='border-b border-ws-border px-3 py-2 text-left font-medium text-ws-muted'>
            {data.xAxisLabel || t('blocks.dataviz.table.xHeader')}
          </th>
          {data.series.map((s, i) => (
            <th key={s.id} scope='col' className='border-b border-ws-border px-3 py-2 text-left font-medium text-ws-muted'>
              {seriesDisplayLabel(s, i, data.series.length, data.yAxisLabel, t)}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {xValues.map((x) => (
          <tr key={String(x)}>
            <td className='border-b border-ws-border px-3 py-2 text-ws-text'>
              {data.xAxisType === 'date' && typeof x === 'number' ? formatDateAxisValue(x) : x}
            </td>
            {data.series.map((s, i) => {
              const y = pointMaps[i]!.get(x);
              return (
                <td key={s.id} className='border-b border-ws-border px-3 py-2 text-ws-text'>
                  {y === undefined ? '—' : y}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
